import produce from 'immer';
import { isEmpty, intersection, findIndex } from 'lodash';
import GuestAffiliation from '../components/manage/guestlist/GuestAffiliation';
import * as ActionTypes from '../actions/types/SeatingChartActionTypes';

import {
  isGuestVisible,
  FACETS_SHAPE,
  FACET_MATCHES_SHAPE,
  makeGuestsByID,
  makeCoupleObject
} from './util/guestRailUtils';

const initialState = {
  groupingsExpanded: {},
  couple: {},
  guestGroupings: null,
  filterState: false,
  currentGroupings: {},
  searchToken: '',
  groupingNames: {},
  facets: FACETS_SHAPE,
  guestsByGuestDistinction: {},
  originalGroupsByGuestId: {},
  guestBySeatUuid: {},
  coupleBySeatUuid: {},
  isFiltering: false,
  searchState: false,
  activeFacetElements: [],
  guestsByID: {},
  facetMatches: FACET_MATCHES_SHAPE
};

/* eslint-disable no-param-reassign, consistent-return */
const guestRailReducer = produce((state = initialState, action) => {
  switch (action.type) {
    // couple, groupingNames, groupingsExpanded, guestGroupings, originalGroupsByGuestId
    case ActionTypes.RECEIVE_CHART: {
      const seatingChart = action.payload.data;
      const coupleObj = seatingChart.guests.couple;
      const guestGroupings = seatingChart.guests.guest_list;
      const { groupingNames, groupingsExpanded } = state;
      const originalGroupsByGuestId = {};

      Object.keys(guestGroupings).forEach((affiliation) => {
        let groupIndex = 0;

        guestGroupings[affiliation].forEach((guestGroup) => {
          guestGroup.guests.forEach((guest) => {
            originalGroupsByGuestId[guest.guest_view.id] = { guestGroup, groupIndex };
          });
          groupIndex += 1;
        });
      });

      // const groupingIdentifiesPopulated = !isEmpty(state.groupingNames);
      const newGroupingNames = { WEDDING_COUPLE: 'The Happy Couple' };
      const newGroupingsExpanded = { WEDDING_COUPLE: true, ...groupingsExpanded };
      if (isEmpty(groupingNames)) {
        const guestAffiliation = GuestAffiliation(
          coupleObj.primary_first_name,
          coupleObj.partner_first_name
        );

        Object.keys(guestGroupings).forEach((affiliation) => {
          newGroupingsExpanded[affiliation] = false;
        });

        for (let i = 0; i < guestAffiliation.length; i += 1) {
          const affiliation = guestAffiliation[i].value;
          newGroupingNames[affiliation] = affiliation === 'UNKNOWN' ? 'Other' : guestAffiliation[i].title;
        }
      }
      return {
        ...state,
        couple: makeCoupleObject(coupleObj),
        guestGroupings,
        originalGroupsByGuestId,
        groupingNames: isEmpty(groupingNames) ? newGroupingNames : groupingNames,
        groupingsExpanded: isEmpty(groupingNames)
          ? newGroupingsExpanded
          : groupingsExpanded,
        guestsByID: makeGuestsByID(guestGroupings)
      };
    }
    // couple, coupleBySeatUuid, currentGroupings, facets, guestsByGuestDistinction,
    // guestBySeatUuid, guestsByID,
    // *** TODO: make immutable
    case ActionTypes.LOAD_GUEST_LIST: {
      const { activeFacetElements, guestGroupings, guestsByID, searchToken } = state;
      const couple = Object.assign({}, state.couple);
      const facets = Object.assign({}, FACETS_SHAPE);
      const coupleBySeatUuid = {};

      // COUPLE: isVisible for couple
      couple.primary.isVisible = isGuestVisible(
        couple.primary,
        activeFacetElements,
        searchToken
      );
      couple.partner.isVisible = isGuestVisible(
        couple.partner,
        activeFacetElements,
        searchToken
      );

      // COUPLEBYSEATUUID: couple info
      if (couple.primary.seat_uuid != null) {
        coupleBySeatUuid[couple.primary.seat_uuid] = 'OWNER';
      }
      if (couple.partner.seat_uuid != null) {
        coupleBySeatUuid[couple.partner.seat_uuid] = 'PARTNER';
      }

      // FACETS: Add the couple to their respective buckets (Seated/Not Seated), Attending and Invited
      facets.invited.buckets.invited.couple.push(couple.primary);
      facets.invited.buckets.invited.couple.push(couple.partner);
      facets.rsvp.buckets.attending.couple.push(couple.primary);
      facets.rsvp.buckets.attending.couple.push(couple.partner);

      if (couple.primary.table_uuid != null) {
        facets.seated.buckets.seated.couple.push(couple.primary);
      } else if (couple.primary.table_uuid == null) {
        facets.seated.buckets.notSeated.couple.push(couple.primary);
      }

      if (couple.partner.table_uuid != null) {
        facets.seated.buckets.seated.couple.push(couple.partner);
      } else if (couple.partner.table_uuid == null) {
        facets.seated.buckets.notSeated.couple.push(couple.partner);
      }

      // iterate through guest information
      const affiliations = Object.keys(guestGroupings);
      const currentGroupings = {};
      affiliations.forEach((affiliation) => {
        currentGroupings[affiliation] = [];
        // transform Affiliations
        guestGroupings[affiliation].forEach((group) => {
          const guestGroup = Object.assign({}, group);
          const updatedGuestGroup = [];
          // process Guests
          guestGroup.guests.forEach((g) => {
            const guest = { ...g, id: g.guest_view.id, couple_role: null };
            guest.isVisible = isGuestVisible(guest, activeFacetElements, searchToken);
            updatedGuestGroup.push(guest);
          });
          guestGroup.guests = updatedGuestGroup;
          currentGroupings[affiliation].push(guestGroup);
        });
      });

      // determine guest visibility
      const newGuestsByID = Object.values(guestsByID).reduce(
        (acc, guest) => ({
          ...acc,
          [guest.id]: {
            ...guest,
            isVisible: isGuestVisible(guest, activeFacetElements, searchToken)
          }
        }),
        {}
      );
      // populate facets
      Object.values(newGuestsByID).forEach((guest) => {
        const rsvpType = guest.rsvp_type;
        if (guest.table_uuid !== null) facets.seated.buckets.seated.guests.push(guest);
        if (guest.table_uuid === null) facets.seated.buckets.notSeated.guests.push(guest);
        if (guest.rsvp_type === null) facets.invited.buckets.notInvited.guests.push(guest);
        if (guest.rsvp_type !== null) {
          facets.invited.buckets.invited.guests.push(guest);
          if (rsvpType === 'ATTENDING') facets.rsvp.buckets.attending.guests.push(guest);
          if (rsvpType === 'DECLINED') facets.rsvp.buckets.declined.guests.push(guest);
          if (rsvpType !== 'ATTENDING' && rsvpType !== 'DECLINED') facets.rsvp.buckets.undecided.guests.push(guest);
        }
      });
      // compose return values:
      const guestsByGuestDistinction = {
        guests: newGuestsByID,
        couple: {
          OWNER: couple.primary,
          PARTNER: couple.partner
        }
      };
      const guestBySeatUuid = Object.values(newGuestsByID).reduce(
        (acc, guest) => (guest.seat_uuid ? { ...acc, [guest.seat_uuid]: guest.id } : acc)
      );

      // return values
      state.couple = couple;
      state.coupleBySeatUuid = coupleBySeatUuid;
      state.currentGroupings = currentGroupings;
      state.facets = facets;
      state.guestBySeatUuid = guestBySeatUuid;
      state.guestsByGuestDistinction = guestsByGuestDistinction;
      state.guestsByID = newGuestsByID;
      break;
      // return {
      //   ...state,
      //   couple,
      //   coupleBySeatUuid,
      //   currentGroupings,
      //   facets,
      //   guestBySeatUuid,
      //   guestsByGuestDistinction,
      //   guestsByID: newGuestsByID
      // };
    }
    // facets
    // *** TODO: make immutable
    case ActionTypes.LOAD_FACET_COUNTS: {
      const { activeFacetElements, facets } = state;

      // Create facets
      // The couple would be empty if "not invited" or "no response/ undecided" is selected
      // Unlike guests, we expect an empty list when the facet is selected, so we shouldn't
      // populate the couple list with both members below (facetGroups.map portion)
      const seatedGuests = { guests: [], couple: [], isSet: false, type: 'seated' };
      const invitedGuests = { guests: [], couple: [], isSet: false, type: 'invited' };
      const rsvpGuests = { guests: [], couple: [], isSet: false, type: 'rsvp' };

      // Create bucket-specific groupings of guests
      let isFiltering = false;

      Object.keys(facets).forEach(facetName => Object.keys(facets[facetName].buckets).forEach((bucketKey) => {
        const bucket = facets[facetName].buckets[bucketKey];
        const bucketGuests = bucket.guests;
        const bucketCouple = bucket.couple;
        const isSelected = activeFacetElements.indexOf(bucketKey) > -1;
        if (isSelected) {
          isFiltering = true; // Set the flag so the guest rail knows at least 1 filter is on
          switch (facets[facetName].key) {
            case 'seated': {
              seatedGuests.guests = seatedGuests.guests.concat(bucketGuests);
              seatedGuests.couple = seatedGuests.couple.concat(bucketCouple);
              seatedGuests.isSet = true;
              break;
            }
            case 'invited': {
              invitedGuests.guests = invitedGuests.guests.concat(bucketGuests);
              invitedGuests.couple = invitedGuests.couple.concat(bucketCouple);
              invitedGuests.isSet = true;
              break;
            }
            default: {
              rsvpGuests.guests = rsvpGuests.guests.concat(bucketGuests);
              rsvpGuests.couple = rsvpGuests.couple.concat(bucketCouple);
              rsvpGuests.isSet = true;
            }
          }
        }
      }));

      const facetGroups = [seatedGuests, invitedGuests, rsvpGuests];

      // if the facet group isn't set above, we have to fill it with the original guest list
      facetGroups.map((facetGroup) => {
        if (!facetGroup.isSet) {
          facetGroup.guests = Object.values(state.guestsByGuestDistinction.guests);
          facetGroup.couple = Object.values(state.guestsByGuestDistinction.couple);
        }
        return facetGroup;
      });

      // This is SC specific.
      const intersectedBuckets = [
        {
          type: 'seated',
          guests: intersection(invitedGuests.guests, rsvpGuests.guests),
          couple: intersection(invitedGuests.couple, rsvpGuests.couple)
        },
        {
          type: 'invited',
          guests: intersection(seatedGuests.guests, rsvpGuests.guests),
          couple: intersection(seatedGuests.couple, rsvpGuests.couple)
        },
        {
          type: 'rsvp',
          guests: intersection(seatedGuests.guests, invitedGuests.guests),
          couple: intersection(seatedGuests.couple, invitedGuests.couple)
        }
      ];

      // Set the facet counts based off the guests
      // Keep binary facets 1 or none (i.e. you can't select both not seated and seated)
      intersectedBuckets.forEach((bucket) => {
        bucket.guests.forEach((guest) => {
          switch (facets[bucket.type].key) {
            case 'seated': {
              const isSelected = activeFacetElements.indexOf('seated') > -1;
              if (guest.table_uuid == null && !isSelected) {
                facets.seated.buckets.notSeated.count += 1;
              } else if (
                guest.table_uuid != null
                && !facets.seated.buckets.notSeated.isSelected
              ) {
                facets.seated.buckets.seated.count += 1;
              }
              break;
            }

            case 'invited': {
              const isSelected = activeFacetElements.indexOf('invited') > -1;
              if (guest.rsvp_type == null && !isSelected) {
                facets.invited.buckets.notInvited.count += 1;
              } else if (
                guest.rsvp_type != null
                && !facets.invited.buckets.notInvited.isSelected
              ) {
                facets.invited.buckets.invited.count += 1;
              }
              break;
            }

            default: {
              if (guest.rsvp_type === 'ATTENDING') {
                facets.rsvp.buckets.attending.count += 1;
              } else if (guest.rsvp_type === 'DECLINED') {
                facets.rsvp.buckets.declined.count += 1;
              } else if (guest.rsvp_type != null) {
                facets.rsvp.buckets.undecided.count += 1;
              }
            }
          }
        });

        // Set the facet counts based off the couple
        bucket.couple.forEach((coupleMember) => {
          switch (facets[bucket.type].key) {
            case 'seated': {
              const isSelected = activeFacetElements.indexOf('seated') > -1;
              if (coupleMember.table_uuid == null && !isSelected) {
                facets.seated.buckets.notSeated.count += 1;
              } else if (
                coupleMember.table_uuid != null
                && !facets.seated.buckets.notSeated.isSelected
              ) {
                facets.seated.buckets.seated.count += 1;
              }
              break;
            }

            case 'invited': {
              const isSelected = activeFacetElements.indexOf('invited') > -1;
              if (!isSelected) {
                facets.invited.buckets.invited.count += 1;
              }
              break;
            }

            default: {
              facets.rsvp.buckets.attending.count += 1;
            }
          }
        });
      });

      state.facets = facets;
      state.isFiltering = isFiltering;
      break;
    }
    // searchState, searchToken
    case ActionTypes.SEARCH_GUEST_LIST: {
      const { searchToken } = action.payload;
      const searchState = searchToken !== '';
      return { ...state, searchState, searchToken: searchToken.toLowerCase() };
    }
    // groupingsExpanded
    case ActionTypes.EXPAND_GUEST_GROUP: {
      const { guestGroup } = action.payload;
      const { groupingsExpanded } = state;
      return {
        ...state,
        groupingsExpanded: {
          ...groupingsExpanded,
          [guestGroup]: !groupingsExpanded[guestGroup]
        }
      };
    }
    // activeFacetElements
    case ActionTypes.TOGGLE_FACET_VALUE: {
      const { bucketType } = action.payload;
      const { activeFacetElements } = state;
      const preExisting = activeFacetElements.indexOf(bucketType) > -1;
      const newActiveFacetsElements = preExisting
        ? activeFacetElements.filter(i => i !== bucketType)
        : activeFacetElements.concat([bucketType]);
      return {
        ...state,
        activeFacetElements: newActiveFacetsElements
      };
    }
    // filterState
    case ActionTypes.TOGGLE_FILTER: {
      const { forceClose } = action.payload;
      return {
        ...state,
        filterState: forceClose ? false : !state.filterState
      };
    }
    // *** couple, coupleBySeatUuid, guestGroupings, guestBySeatUuid
    // TODO: make immutable
    case ActionTypes.UPDATE_GUEST_LIST: {
      const { seats } = action.payload;
      if (seats.length === 0) return state;

      // const { guestGroupings } = state;
      // const { guestBySeatUuid } = state;
      // const { coupleBySeatUuid } = state;
      const couple = { ...state.couple };
      const coupleBySeatUuid = { ...state.coupleBySeatUuid };
      const guestGroupings = { ...state.guestGroupings };
      const guestBySeatUuid = { ...state.guestBySeatUuid };

      // We need to sort here to do the deleted changes first, otherwise we might set a guest
      // that moved table to table to the new seat uuid first, then null and they'd appear unseated even if they are seated
      seats.sort((seat1, seat2) => {
        if (seat1.guest_id == null && seat1.couple_role == null) {
          return -1;
        }
        if (seat2.guest_id == null && seat2.couple_role == null) {
          return 1;
        }
        return 0;
      });

      // Update table and seat values of guests in the rail
      seats.forEach((seat) => {
        if (state.coupleBySeatUuid[seat.uuid] != null || seat.couple_role != null) {
          // It is a couple action
          const coupleRole = state.coupleBySeatUuid[seat.uuid]; // Not null if a couple member was at the seat (i.e we're removing or moving table to table)
          const tableUuid = coupleRole != null ? null : seat.table_uuid;
          const seatUuid = coupleRole != null ? null : seat.uuid;
          // set the couple member's table & seat uuids
          if (seat.couple_role === 'OWNER' || coupleRole === 'OWNER') {
            couple.primary.table_uuid = tableUuid;
            couple.primary.seat_uuid = seatUuid;
          } else {
            couple.partner.table_uuid = tableUuid;
            couple.partner.seat_uuid = seatUuid;
          }
          if (coupleRole != null) {
            // delete the stored seat
            delete coupleBySeatUuid[seat.uuid];
          } else {
            // Adding a couple to a seat, set the seat
            coupleBySeatUuid[seat.uuid] = seat.couple_role;
          }
        } else if (state.guestBySeatUuid[seat.uuid] != null || seat.guest_id != null) {
          // It is a guest action
          const removeGuestId = state.guestBySeatUuid[seat.uuid]; // Not null if the guest was at the seat (i.e we're removing or moving table to table)
          const tableUuid = removeGuestId != null ? null : seat.table_uuid;
          const seatUuid = removeGuestId != null ? null : seat.uuid;
          const guestId = removeGuestId != null ? removeGuestId : seat.guest_id;
          const { guestGroup } = state.originalGroupsByGuestId[guestId];
          const guestGroupIndex = state.originalGroupsByGuestId[guestId].groupIndex;
          const guestIndex = findIndex(
            guestGroup.guests,
            guest => guest.guest_view.id === guestId
          );

          if (guestIndex > -1) {
            // Should always be true
            const guest = guestGroup.guests[guestIndex];
            guest.table_uuid = tableUuid;
            guest.seat_uuid = seatUuid;
            guestGroupings[guestGroup.guest_affiliation][guestGroupIndex].guests[
              guestIndex
            ] = guest;
          }

          if (removeGuestId != null) {
            // Remove the stored seat
            delete guestBySeatUuid[seat.uuid];
          } else {
            // Add the stored seat
            guestBySeatUuid[seat.uuid] = seat.guest_id;
          }
        }
      });

      state.couple = couple;
      state.coupleBySeatUuid = coupleBySeatUuid;
      state.guestGroupings = guestGroupings;
      state.guestBySeatUuid = guestBySeatUuid;
      break;
    }

    default:
      return state;
  }
});

export default guestRailReducer;



// WEBPACK FOOTER //
// ./src/reducers/guestRail.js